import { useQueryClient } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Globe } from "lucide-react";
import { DropdownMenu } from "radix-ui";
import { useApi } from "../api/client";
import { qk } from "../api/queries";
import type { Session } from "../api/types";
import { common } from "../i18n/common";
import { LOCALE_NAMES, LOCALES, setLocale, tNow, useLocale, useT, type Locale } from "../i18n/core";
import { useToast } from "../ui/Toasts";

/**
 * Seletor de idioma. Com sessão, grava a escolha na conta; sem sessão (tela
 * de entrada), vale para este navegador.
 */
export function LocaleMenu({ variant }: { variant: "rail" | "plain" }) {
  const locale = useLocale();
  const t = useT(common);
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();

  async function choose(next: Locale) {
    if (next === locale) return;
    setLocale(next);
    // Mensagens montadas pela API (erros, atividade, auditoria) seguem o idioma do pedido.
    void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== qk.session[0] });
    const session = qc.getQueryData<Session>(qk.session);
    if (!session) return;
    try {
      const saved = await api.updatePreferences({ locale: next });
      qc.setQueryData<Session>(qk.session, { ...session, user: { ...session.user, locale: saved.locale } });
    } catch {
      toast("error", tNow(common).languageSaveError);
    }
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button type="button" className={`locale-switch ${variant === "rail" ? "on-rail" : "plain"}`} aria-label={`${t.language}: ${LOCALE_NAMES[locale]}`}>
          <Globe aria-hidden="true" />
          <span>{LOCALE_NAMES[locale]}</span>
          <ChevronsUpDown aria-hidden="true" className="chev" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="menu menu-narrow" sideOffset={6} side={variant === "rail" ? "top" : "bottom"} align={variant === "rail" ? "start" : "end"} collisionPadding={8}>
          <DropdownMenu.Label className="menu-label">{t.language}</DropdownMenu.Label>
          {LOCALES.map((l) => (
            <DropdownMenu.Item key={l} className="menu-item" lang={l} onSelect={() => void choose(l)}>
              {LOCALE_NAMES[l]}
              {l === locale && <Check aria-hidden="true" className="meta" size={15} />}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
